/**
 * Device Utils
 *
 * 디바이스 타입 감지 및 관련 유틸리티 함수들
 */

import { Channel } from 'scbk-services/config';
import { Device } from 'scbk-services/config/enums/device';

/**
 * User Agent를 기반으로 디바이스 타입을 감지하는 함수
 *
 * @returns 감지된 디바이스 타입
 *
 * @description
 * 1. 태블릿 감지: tablet, ipad, android(모바일이 아닌 경우)
 * 2. 모바일 감지: mobile, android, iphone, ipod, blackberry, iemobile, opera mini
 * 3. 기본값: PC
 *
 * @example
 * ```tsx
 * const deviceType = getDeviceType();
 * console.log(deviceType); // 'PC', 'MOBILE', 'TABLET'
 * ```
 */
export const getDeviceType = (): Device => {
    const userAgent = navigator.userAgent.toLowerCase();

    // 태블릿 감지
    if (/tablet|ipad|android(?!.*mobile)/i.test(userAgent)) {
        if (/ma30/gi.test(userAgent)) {
            return Device.APP;
        }
        return Device.TABLET;
    }

    // 모바일 감지
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
        if (/ma30/gi.test(userAgent)) {
            return Device.APP;
        }
        return Device.MOBILE;
    }

    // 기본값: PC
    return Device.PC;
};

/**
 * 현재 디바이스가 앱인지 확인하는 함수
 * @returns
 */
export const isApp = (): boolean => getDeviceType() === Device.APP;

/**
 * 현재 디바이스가 모바일인지 확인하는 함수
 *
 * @returns 모바일 여부
 *
 * @example
 * ```tsx
 * if (isMobile()) {
 *   // 모바일 전용 로직
 * }
 * ```
 */
export const isMobile = (): boolean => getDeviceType() === Device.MOBILE;

/**
 * 현재 디바이스가 PC인지 확인하는 함수
 *
 * @returns PC 여부
 *
 * @example
 * ```tsx
 * if (isPC()) {
 *   // PC 전용 로직
 * }
 * ```
 */
export const isPC = (): boolean => getDeviceType() === Device.PC;

/**
 * 현재 디바이스가 태블릿인지 확인하는 함수
 *
 * @returns 태블릿 여부
 *
 * @example
 * ```tsx
 * if (isTablet()) {
 *   // 태블릿 전용 로직
 * }
 * ```
 */
export const isTablet = (): boolean => getDeviceType() === Device.TABLET;

/**
 *
 * @returns
 */
export const isIOS = () => {
    if (typeof navigator === 'undefined') return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
};

/**
 *
 * @returns
 */
export const isAndroid = () => {
    if (typeof navigator === 'undefined') return false;
    return /android/i.test(navigator.userAgent.toLowerCase());
};

/**
 * Window OS 여부
 * @returns
 */
export const isWin = () => {
    if (typeof navigator === 'undefined') return false;
    return navigator.userAgent.indexOf('Win') != -1;
};

/**
 * Mac OS 여부
 * @returns
 */
export const isMac = () => {
    if (typeof navigator === 'undefined') return false;
    return navigator.userAgent.indexOf('Mac') != -1;
};

/**
 * 디바이스 타입에 따른 플랫폼 값을 반환하는 함수
 *
 * @returns 플랫폼 값 ('IB' 또는 'SB')
 *
 * @description
 * - PC: 'IB'
 * - 모바일/태블릿: 'SB'
 *
 * @example
 * ```tsx
 * const platform = getPlatformByDevice();
 * console.log(platform); // 'IB' 또는 'SB'
 * ```
 */
export const getPlatformByDevice = (): string => {
    const deviceType = getDeviceType();
    if (deviceType === Device.APP) return Channel.MB;
    if (deviceType === Device.MOBILE || deviceType === Device.TABLET) {
        if (import.meta.env.VITE_ENVIRONMENT === 'local') {
            return Channel.MB;
        }
        return Channel.MW;
    }
    return Channel.IB;
};
